<template>
  <div>
    <b-table
      id="#my-leads-table"
      ref="buyLeadsTable"
      class="b-table bg-white border table-br-bottom-0 shadow table"
      hover
      striped
      show-empty
      :empty-text="noLeadsText"
      :no-border-collapse="true"
      :items="leadsProvider"
      :fields="fields"
      :busy.sync="busy"
      :per-page="pageSize"
      :current-page="page"
      :selectable="true"
      :select-mode="'single'"
      :tbody-tr-attr="setRowAttributes"
      :tbody-tr-class="setRowStyle"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :no-local-sorting="true"
      :sort-icon-left="true"
      :no-sort-reset="true"
      @row-selected="onRowSelected"
    >
      <template v-slot:cell(date)="data">
        <div class="first-col">
          <b-badge
            v-if="!olderThanNow(data.item.priority_access)"
            class="priority-badge d-flex align-items-center rounded"
          >
            <span class="float-left" v-html="$t('Priority Access')" />
            <radial-progress-bar
              :class="'ml-1'"
              :strokeWidth="2"
              :innerStrokeWidth="2"
              :completed-steps="3"
              :diameter="20"
              :startColor="'#fff'"
              :stopColor="'#fff'"
              :innerStrokeColor="'#013652'"
              :total-steps="12"
            />
          </b-badge>
          <div :class="[{'priority-lead': !olderThanNow(data.item.priority_access)}, data.item.viewed === 0 ? 'font-weight-600' : 'font-italic']">
            {{ $d(parseJsonDate(data.item.dateReleased), 'short') }}<br>{{ $d(parseJsonDate(data.item.dateReleased), 'time') }}
          </div>
        </div>
      </template>
      <template v-slot:cell(postcode)="data">
        <div class="d-flex justify-content-start align-items-center">
          <span
            v-if="data.item.inCoverage===1"
            :class="['status-icon', 'mx-1', 'text-success']"
            title="Within Coverage Area"
          >
            <font-awesome-icon :icon="'circle'" />
          </span>
          <span v-else :class="['status-icon', 'mx-1', 'text-info']" title="Outside Coverage Area">
            <font-awesome-icon :icon="'circle'" />
          </span>
          <span
            :class="[data.item.viewed === 0 ? 'font-weight-600' : 'font-italic', 'ml-2', 'postcode']"
            v-html="leadShortAddressHtml(data.item.location, data.item[0].postcode, data.item.location_url)"
          />
        </div>
      </template>
      <template v-slot:cell(workType)="data">
        <span :class="data.item.viewed === 0 ? 'font-weight-600' : 'font-italic'">
          {{ data.item.work_type_translation }}
        </span>
      </template>
      <template v-slot:cell(timescale)="data">
        <span :class="data.item.viewed === 0 ? 'font-weight-600' : 'font-italic'">
          {{ getTimescaleTranslation(data.item[0].question_answers) }}
        </span>
      </template>
      <template v-slot:cell(sold)="data">
        <span :class="data.item.viewed === 0 ? 'font-weight-600' : 'font-italic'">
          {{
            $t(
              '{n}/{max} {cta}',
              {
                n: data.item[0].sold,
                max: data.item[0].number_leads_requested,
                cta: leadsSold(data.item[0].sold, data.item[0].number_leads_requested)
              }
            )
          }}
        </span>
      </template>
      <template v-slot:cell(qualified)="data">
        <span :class="['text-success', 'd-flex', 'justify-content-center']">
          <font-awesome-icon v-if="data.item.qualified === 1" size="lg" icon="user-check" />
          <font-awesome-icon v-else size="lg" :icon="['far', 'check-circle']" />
        </span>
      </template>
      <template v-slot:cell(dashboardPrice)="data">
        <span class="text-center" :class="data.item.viewed === 0 ? 'font-weight-600' : 'font-italic'">
          {{ $n(data.item.dashboardPrice, 'currency') }}
        </span>
      </template>
      <template v-slot:cell(buy)="data">
        <array-checkbox v-if="bulk" v-model="selectedLeads" :val="data.item" :limit="5" :disabled="disable">
          {{ $t('Buy') }}
        </array-checkbox>
        <b-button v-else size="sm" class="btn-lead-buy" variant="outline-primary" @click="selectRow(data)">
          {{ data.rowSelected ? $t('Close') : $t('View') }}
        </b-button>
      </template>
      <template v-slot:table-busy>
        <spinner :display-loading="true" />
      </template>
    </b-table>
    <div class="d-flex justify-content-between">
      <b-select v-if="items > 20" v-model="pageSize" :options="pageSizeOptions" class="w-auto page-size" />
      <b-pagination
        v-if="items/pageSize > 1"
        v-model="page"
        :total-rows="items"
        :per-page="pageSize"
        aria-controls="my-leads-table"
        align="right"
      />
    </div>
  </div>
</template>

<script>
import {BUY_LEADS_RESULT_COUNT, CLEAR_PRE_SELECT} from "@/store/mutation-types";
import Spinner from "@/components/animation/Spinner";
import ArrayCheckbox from "@/components/form/elements/ArrayCheckbox";
import {ACTIONED_LEADS, CLEAR_SELECTED_LEADS, PRE_SELECT, SELECTED_LEADS} from "@/store/leads/mutation-types";
import LeadsSoldMixin from "@/imports/mixins/leadsSold";
import LeadShortAddressMixin from "@/imports/mixins/leadShortAddress";
import OlderThanNow from "@/imports/mixins/olderThanNow";
import RadialProgressBar from 'vue-radial-progress';
import gaEvent from "@/imports/mixins/gaEvent";
import dates from '@/services/date';
import company from "@/imports/mixins/company";
import workTypeTranslation from "@/imports/mixins/workTypeTranslation";
import { captureError } from "@/services/userMonitoring";

export default {
  name: "MarketplaceLeadsTable",
  components: {ArrayCheckbox, Spinner, RadialProgressBar},
  mixins:[LeadsSoldMixin, LeadShortAddressMixin, OlderThanNow, gaEvent, company, workTypeTranslation],
  props:['fields', 'disable'],
  data() {
    return {
      busy: false,
      items: 0,
      filterKey: 'buyLeads',
      selectedLead: null,
      sortBy: null,
      sortDesc: true,
    }
  },
  computed: {
    preSelect: function() {
      return this.$store.state.leads.PRE_SELECT;
    },
    filter: function() {
      return this.$store.getters.getFilter(this.filterKey);
    },
    selectedLeads: {
      get: function() {
        return this.$store.state.leads.SELECTED_LEADS;
      },
      set: function (value) {
        this.$store.commit(SELECTED_LEADS, value);
      }
    },
    bulk: function() {
      return this.$store.state.leads.BULK_BUY;
    },
    isBannedMarketplace: function() {
      return (this.company) ? this.company.no_marketplace : false;
    },
    isBannedMarketplaceWidening: function() {
      return (this.company) ? this.company.no_widen_marketplace : false;
    },
    noLeadsText: function (){
      let text;

      if (this.isBannedMarketplace) {
        text = this.$t('There are currently no leads available. Please keep an eye out for any Priority Lead Alerts');
      } else if (this.isBannedMarketplaceWidening) {
        text = this.$t('There are no leads available for you to buy at this time in this area');
      } else {
        text = this.$t('No leads matching your search criteria were found. Please adjust the filters and try again');
      }

      return text;
    },
    actionedLeads: {
      get: function() {
        return this.$store.state.leads.ACTIONED_LEADS;
      },
      set: function(value) {
        this.$store.commit(ACTIONED_LEADS, value);
      }
    },
    page: {
      get: function() {
        return this.$route.query.page ?? 1;
      },
      set: function(value) {
        if ((this.$route.query.page && this.$route.query.page !== value) || (this.$route.query.page === undefined && value !== 1)) {
          this.$router.push({name:'buy-leads', query:{...this.$route.query, page:value}});
        }
      }
    },
    sortOrder: function() {
      return this.sortBy ? (this.sortBy + '-' + (this.sortDesc ? 'desc' : 'asc')) : null;
    },
    pageSize: {
      get: function() {
        return this.$store.state.BUY_LEADS_RESULT_COUNT;
      },
      set: function(value) {
        this.$store.commit(BUY_LEADS_RESULT_COUNT, value);
      }
    },
    pageSizeOptions: function() {
      return [
        {value: 10, text: this.$tc("Show {n}", 10)},
        {value: 20, text: this.$tc("Show {n}", 20)},
        {value: 40, text: this.$tc("Show {n}", 40)},
        {value: 60, text: this.$tc("Show {n}", 60)},
      ]
    },
  },
  methods: {
    parseJsonDate: dates.parseJsonDate,
    leadsProvider: async function() {
      return this.$api.leads.marketplace({...this.filter, page:this.page, pageSize:this.pageSize, order:this.sortOrder})
        .then((result) => {
          this.setMeta(result.data.meta);
          if (this.preSelect && result.data.meta.preSelect !== null) {
            this.selectedLead = result.data.data.findIndex((lead) => lead[0].id === result.data.meta.preSelect);
          }
          result.data.data.forEach((lead) => lead[0].sold = Math.max(lead[0].sold, 0));
          return result.data.data;
        }).catch((e) => {
          if (e.response && e.response.status === 308) {
            this.$router.push({name:'my-lead', params: {code:e.response.data.redirect}});
          } else {
            captureError(e, {
              section: "marketplace",
              action: "get-leads",
              type: "api-request"
            });
          }
        });
    },
    setRowAttributes: function(item, type) {
      if (!item || type !== 'row') return;
      return {
        title: 'Qualification notes: ' + (item[0].description !== null && item[0].description.length > 0 ? item[0].description : 'none provided'),
        style: "cursor: pointer;",
      }
    },
    setRowStyle: function(item, type) {
      if (!item || type !== 'row') return;
      let trClass = [];
      if (this.isActioned(item[0].id)) {
        trClass.push('d-none');
      }
      if (this.bulk) {
        trClass.push('cursor-default');
        trClass.push('no-highlight');
      }
      return trClass.join(' ');
    },
    setMeta: function(metaData) {
      this.items = metaData.totalResults;
      this.pageSize = metaData.pageSize;
    },
    buyLead: function(lead) {
      this.$store.dispatch('addLeadToBasket', lead);
    },
    refresh: function() {
      this.actionedLeads = [];
      this.$refs.buyLeadsTable.refresh();
      this.removeOrigin();
    },
    clearSelected: function() {
      this.$store.commit(CLEAR_SELECTED_LEADS);
      this.removeOrigin();
    },
    onRowSelected: function(items) {
      if (!this.bulk) {
        if(items.length > 0) {
          this.buyLead(items[0]);
          if (items[0].viewed === 0) {
            items[0].viewed = 1;
            this.$api.leads.markViewed(items[0][0].id);
          }
        } else {
          this.clearSelected();
        }
      } else {
        if (items.length > 0 && this.selectedLeads.length < 5 && !this.disable) {
          this.$store.dispatch('addLeadToBasket', items[0]);
        }
      }
    },
    selectRow(rowData) {
      if(!this.$store.state.leads.BULK_BUY) {
        if(!rowData.rowSelected) {
          this.$refs.buyLeadsTable.selectRow(rowData.index);
        } else {
          this.$refs.buyLeadsTable.unselectRow(rowData.index);
        }
        this.removeOrigin();
      } else {
        this.buyLead(rowData.items[0]);
      }
    },
    preSelectLead: function() {
      if (this.preSelect && this.selectedLead !== null && !this.bulk) {
        this.$refs.buyLeadsTable.selectRow(this.selectedLead);
        this.$store.commit(PRE_SELECT, false);
        this.selectedLead = null;
        this.$store.commit(CLEAR_PRE_SELECT, this.filterKey);
      }
    },
    isActioned: function(lead) {
      return this.actionedLeads.find((actionedLead) => lead === actionedLead) !== undefined;
    },
  },
  watch: {
    filter: function() {
      this.page = 1;
      this.removeOrigin();
      this.refresh();
    },
    selectedLeads: function(value) {
      if (value.length < 1 && !this.bulk) {
        this.$refs.buyLeadsTable.selectedRows = [];
      }
    },
    busy: function (value) {
      if (value === false) {
        this.preSelectLead();
      }
    }
  }
}
</script>

<style scoped lang="scss">
.first-col {
  position: relative;
  padding-left: 1rem;

  .priority-badge {
    font-size: 10px;
    position: absolute;
    padding: 4px;
    background: linear-gradient(to right, $notification-icon-color, $priority-light-color);
    box-shadow: 0 3px 6px rgba(0, 0, 0, .16);
    color: $theme-primary;
    transform: scale(.8);
    @media only screen and (max-width: 1200px) {
      top: -5px;
    }

    @media only screen and (min-width: 1201px) and (max-width: 1399px) {
      left: -40px;
      top: 7px;
    }

    @media only screen and (max-width: 1399px) {
      transform: scale(.8);
    }

    @media only screen and (min-width: 1400px) {
      transform: scale(.9);
      left: -50px;
      top: 7px;
    }
  }

  .priority-lead {
    @media only screen and (max-width: 1200px) {
      padding-top: 25px;
    }
    @media only screen and (min-width: 1200px) {
      padding-left: 10px;
    }
  }
}

.page-size {
  border-width: 1px;
  padding-right: 30px;
  background-position: right 10px bottom 11px;

  &:focus {
    border-color: $border-color;
  }
}

::v-deep table thead .first-col {
  background-position: left 1rem center !important;

  div {
    padding-left: 1rem;
  }
}

.table-active {
  .btn-outline-primary {
    color: $theme-primary;
    border-color: $border-color;
    background: $white;

    &:hover {
      background-color: $theme-secondary-green-900;
      border-color: $theme-secondary-green-900;
      color: $white;
    }
  }
}

@media only screen and (min-width: 1029px) and (max-width: 1200px)  {
  ::v-deep table {
    thead .first-col {
      background-position: left .5rem center !important;
    }
    thead .first-col div,
    .first-col {
      padding-left: .5rem;
    }

    td,
    th {
      padding-left: .3rem;
      padding-right: .3rem;
    }
  }

  .basket .card-body {
    padding: 1rem !important;
  }
}

@media only screen and (min-width: 1400px) and (max-width: 1550px) {
  ::v-deep table {
    thead .first-col div,
    .first-col {
      padding-left: .5rem;
    }

    td,
    th {
      padding-left: .3rem;
      padding-right: .3rem;
    }
  }

  .basket .card-body {
    padding: 1rem !important;
  }
}
</style>
